import { inject,Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
    graphql,
    Library,
    MediaclipMutationReleaseLibraryLockArgs,
    MediaclipMutationStartUpdateLibraryArgs,
    MediaclipMutationUpdateLibraryMetadataArgs,
    MediaclipMutationUpdateLibraryUpdateSchedulesArgs,
    ReleaseLibraryLockInput,
    StartUpdateLibrary,
    StartUpdateLibraryInput,
    UpdateLibraryMetadataInput,
    UpdateLibraryScheduleUpdatesInput
} from 'app/shared/graph';
import { GraphDotnetService } from 'app/shared/graph';

@Injectable({ providedIn: 'root' })
export class LibraryUpdateService {
    private readonly graphDotnetService = inject(GraphDotnetService);


    getLibraries(): Observable<Library[]> {
        return this.graphDotnetService.query(graphql`query {
        libraries {
            owner
            branches {
              name
              metadata {
                version
              }
            }
          }
        }`).pipe(map(q => q.libraries));
    }

    getLibraryStatus(owner: string): Observable<Library> {
        return this.graphDotnetService.query(graphql`
        query loadLibrary($owner: String!) {
            library(owner: $owner) {
                owner
                gitBranches {
                    name
                    commitHash
                }
                branches {
                  name
                  scheduledUpdates {
                    gitBranch
                    schedules {
                      hourUtc
                      weekDays
                    }
                  }
                  metadata {
                    version
                    gitBranch
                    doNotValidate
                    validationDefaultPackage
                    validationCulture
                    progress {
                      progressSignalRUrl
                      updateId
                      stage
                      lastUpdateDateUtc
                      completed
                      max
                      value
                      errorMessage
                      totalDuration
                      logs {
                        stage
                        duration
                      }
                      hubValidation {
                        logs {
                          level
                          message
                        }
                      }
                      validation {
                        entries {
                          level
                          category
                          package
                          url
                          message
                        }
                      }
                    }
                  }
                }
              }
            }`, { owner })
            .pipe(map(q => q.library));
    }

    startUpdateLibrary(input: StartUpdateLibraryInput): Observable<StartUpdateLibrary> {
        return this.graphDotnetService.mutate<MediaclipMutationStartUpdateLibraryArgs>(graphql`
          mutation startUpdateLibrary($input: StartUpdateLibraryInput!) {
            startUpdateLibrary(input: $input) {
              updateId
              message
              progressPollingEndpointUrl
              progressSignalRUrl
            }
          }`, { input }
        ).pipe(map(x => x.startUpdateLibrary));
    }

    releaseLibraryLock(input: ReleaseLibraryLockInput): Observable<StartUpdateLibrary> {
        return this.graphDotnetService.mutate<MediaclipMutationReleaseLibraryLockArgs>(graphql`
          mutation releaseLibraryLock($input: ReleaseLibraryLockInput!) {
            releaseLibraryLock(input: $input)
          }`, { input }
        ).pipe(map(x => x.releaseLibraryLock));
    }

    updateLibraryMetadata(input: UpdateLibraryMetadataInput): Observable<void> {
        return this.graphDotnetService.mutate<MediaclipMutationUpdateLibraryMetadataArgs>(graphql`
          mutation updateLibraryMetadata($input: UpdateLibraryMetadataInput!) {
            updateLibraryMetadata(input: $input)
          }`, { input }
        ).pipe(map(x => x.updateLibraryMetadata));
    }

    updateLibraryUpdateSchedules(input: UpdateLibraryScheduleUpdatesInput): Observable<void> {
        return this.graphDotnetService.mutate<MediaclipMutationUpdateLibraryUpdateSchedulesArgs>(graphql`
          mutation updateLibraryUpdateSchedules($input: UpdateLibraryScheduleUpdatesInput!) {
            updateLibraryUpdateSchedules(input: $input)
          }`, { input }
        ).pipe(map(x => x.updateLibraryUpdateSchedules));
    }
}
